/** How the "escanear" action acquires a document on this device. */
export type CaptureMode =
  /** Hand off to the OS camera app through `<input capture>`. */
  | 'native'
  /** In-page `getUserMedia` preview against an attached camera. */
  | 'camera'
  /** Plain file chooser — no camera reachable from the browser. */
  | 'file';

/** localStorage key that pins a mode, for trying a station without a rebuild. */
const OVERRIDE_KEY = 'aleia.capture_mode';

/**
 * Picks the capture strategy for the current device.
 *
 * Tablets and phones keep the native camera app they have always used: it gives
 * autofocus, a full-screen viewfinder and its own retake step, all of which beat
 * anything an in-page preview can do. Desktops silently ignore the `capture`
 * attribute, so a kiosk PC with a USB webcam would otherwise get nothing but a
 * file-chooser dialog — those go through `getUserMedia` instead.
 */
export function detectCaptureMode(): CaptureMode {
  const forced = readOverride();
  if (forced) return forced;
  if (isMobileOs()) return 'native';
  return hasCameraApi() ? 'camera' : 'file';
}

function readOverride(): CaptureMode | null {
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(OVERRIDE_KEY);
  } catch {
    return null; // Storage blocked (private mode, kiosk policy) — not an error.
  }
  return raw === 'native' || raw === 'camera' || raw === 'file' ? raw : null;
}

function isMobileOs(): boolean {
  const ua = navigator.userAgent;
  if (/Android|iPhone|iPod|iPad/i.test(ua)) return true;
  // iPadOS 13+ ships a desktop user agent; the touch points give it away.
  return /Macintosh/.test(ua) && navigator.maxTouchPoints > 1;
}

function hasCameraApi(): boolean {
  // `getUserMedia` is only exposed on a secure origin. Checking rather than
  // catching keeps an http:// deployment on the file chooser instead of
  // dead-ending on a permission error the operator cannot act on.
  return window.isSecureContext && !!navigator.mediaDevices?.getUserMedia;
}
